import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { timetableService, timetableTimeOrder, schoolClassSortOrder } from '../services/TimetableService';
import './AdminTimetableView.css';
import './AdminMoreDetailPages.css';
import { scopeBySchool } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// School-scoped cache key builder — shared with the analytics/admin views so
// tabs reuse each other's fetched collections.
const ckFor = (schoolId) => (p) => `${String(schoolId || 'all').toLowerCase()}:${p}`;

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Fixed 10-slot school day (matches iOS AdminTimetableView)
const TIME_SLOTS = [
  { start: '8:30 AM', end: '9:15 AM' },    // 0: subject
  { start: '9:15 AM', end: '10:00 AM' },   // 1: subject
  { start: '10:00 AM', end: '10:15 AM' },  // 2: break
  { start: '10:15 AM', end: '11:00 AM' },  // 3: subject
  { start: '11:00 AM', end: '12:00 PM' },  // 4: subject
  { start: '12:00 PM', end: '1:00 PM' },   // 5: lunch
  { start: '1:00 PM', end: '1:45 PM' },    // 6: subject
  { start: '1:45 PM', end: '2:15 PM' },    // 7: subject
  { start: '2:15 PM', end: '2:30 PM' },    // 8: break
  { start: '2:30 PM', end: '3:15 PM' }     // 9: subject
];
const LUNCH_SLOT_INDEX = 5;

const ENTRY_TYPES = [
  { value: 'subject', label: 'Subject', icon: '📚', color: 'blue' },
  { value: 'lunch', label: 'Lunch', icon: '🍽️', color: 'green' },
  { value: 'break', label: 'Break', icon: '☕', color: 'purple' }
];

function entryTypeInfo(entryType) {
  return ENTRY_TYPES.find(t => t.value === entryType) || ENTRY_TYPES[0];
}

/// Convert "h:mm AM/PM" string to minutes-since-midnight, or null.
function timeToMinutes(time12h) {
  if (!time12h) return null;
  return timetableTimeOrder(time12h);
}

/// Convert a Date to "h:mm AM/PM" string.
function dateToTime12h(date) {
  let h = date.getHours();
  const m = date.getMinutes();
  const period = h < 12 ? 'AM' : 'PM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${period}`;
}

/// Convert "h:mm AM/PM" string to a Date (today, with the given hour/minute).
function time12hToDate(time12h) {
  const mins = timeToMinutes(time12h);
  if (mins == null) return new Date();
  const d = new Date();
  d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
  return d;
}

function AdminTimetableView({ schoolId }) {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [timetableStatus, setTimetableStatus] = useState('Draft');
  const [publishedAt, setPublishedAt] = useState(null);
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = new Date().getDay(); // 1=Mon, ..., 5=Fri
    return WEEKDAYS[today - 1] || 'Monday';
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  // Slot editor modal state
  const [activeSlot, setActiveSlot] = useState(null);       // {start, end}
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);
  const [showSlotEditor, setShowSlotEditor] = useState(false);

  // Extra custom slots beyond the fixed 10
  const [extraSlots, setExtraSlots] = useState([]);

  // Slot detail page state { entry, slot, index }
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailTeacherName, setDetailTeacherName] = useState('');

  // Resolve teacher name for the detail page
  useEffect(() => {
    setDetailTeacherName('');
    const tid = selectedEntry?.entry?.teacherId;
    if (!tid) return;
    let cancelled = false;
    (async () => {
      try {
        const u = await dataCacheService.getDoc(ckFor(schoolId)(`users/${tid}`), doc(db, 'users', tid));
        if (cancelled) return;
        if (u) {
          setDetailTeacherName(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || tid);
        } else {
          setDetailTeacherName(tid);
        }
      } catch {
        if (!cancelled) setDetailTeacherName(tid);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEntry, schoolId]);

  const loadAllClasses = useCallback(async () => {
    try {
      const classesData = scopeBySchool(
        await dataCacheService.getDocs(ckFor(schoolId)('classes'), collection(db, 'classes')),
        schoolId
      );
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
      if (classesData.length > 0 && !selectedClassId) {
        setSelectedClassId(classesData[0].id);
      }
    } catch (err) {
      console.error('AdminTimetableView: error loading classes', err);
      setError('Failed to load classes');
    }
  }, [schoolId, selectedClassId]);

  const loadTimetable = useCallback(async (classId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const timetable = await timetableService.getTimetable(classId);
      setEntries(timetable.entries || []);
      setTimetableStatus(timetable.status || 'Draft');
      setPublishedAt(timetable.publishedAt || null);
    } catch (err) {
      console.error('AdminTimetableView: error loading timetable', err);
      setError('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllClasses();
  }, [loadAllClasses]);

  useEffect(() => {
    if (selectedClassId) {
      loadTimetable(selectedClassId);
    } else {
      setEntries([]);
      setTimetableStatus('Draft');
      setPublishedAt(null);
    }
  }, [selectedClassId, loadTimetable]);

  const isReadOnly = timetableStatus === 'Published' || timetableStatus === 'Archived';

  // Persist entries to Firestore (used after add/update/remove)
  const persistEntries = useCallback(async (updatedEntries) => {
    if (!selectedClassId) return;
    try {
      await timetableService.saveTimetableWithEntries(selectedClassId, updatedEntries, schoolId || null, timetableStatus);
    } catch (err) {
      console.error('AdminTimetableView: error saving timetable', err);
      setError('Failed to save timetable: ' + err.message);
    }
  }, [selectedClassId, schoolId, timetableStatus]);

  const handlePublish = async () => {
    setShowPublishConfirm(false);
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await timetableService.publishTimetable(selectedClassId, schoolId || 'admin');
      setTimetableStatus('Published');
      setSuccess('Timetable published successfully');
      await loadTimetable(selectedClassId);
    } catch (err) {
      setError('Failed to publish timetable: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await timetableService.archiveTimetable(selectedClassId);
      setTimetableStatus('Archived');
      setSuccess('Timetable archived successfully');
      await loadTimetable(selectedClassId);
    } catch (err) {
      setError('Failed to archive timetable: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevert = async () => {
    setShowRevertConfirm(false);
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await timetableService.revertToDraft(selectedClassId);
      setTimetableStatus('Draft');
      setPublishedAt(null);
      setSuccess('Timetable reverted to draft');
      await loadTimetable(selectedClassId);
    } catch (err) {
      setError('Failed to revert timetable: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // All slots: fixed + extra, each with its global index
  const allSlots = useMemo(() => {
    const result = [];
    TIME_SLOTS.forEach((s, i) => result.push({ slot: s, index: i }));
    extraSlots.forEach((s, i) => result.push({ slot: s, index: TIME_SLOTS.length + i }));
    return result;
  }, [extraSlots]);

  const dayEntries = useMemo(
    () => entries.filter(e => e.dayOfWeek === selectedDay),
    [entries, selectedDay]
  );

  const entriesBySlotIndex = useMemo(() => {
    const map = {};
    dayEntries.forEach(e => { map[e.slotIndex] = e; });
    return map;
  }, [dayEntries]);

  // Entries that don't fit any slot (fixed or extra)
  const slotsCount = TIME_SLOTS.length + extraSlots.length;
  const extraEntries = useMemo(
    () => dayEntries
      .filter(e => e.slotIndex >= slotsCount)
      .sort((a, b) => timetableTimeOrder(a.startTime) - timetableTimeOrder(b.startTime)),
    [dayEntries, slotsCount]
  );

  const openSlotEditor = (slot, index) => {
    if (isReadOnly) return;
    if (index === LUNCH_SLOT_INDEX) return; // lunch is always lunch
    setActiveSlot(slot);
    setActiveSlotIndex(index);
    setShowSlotEditor(true);
  };

  const handleSaveEntry = async (entry) => {
    let updated;
    const existingIdx = entries.findIndex(e => e.id === entry.id);
    if (existingIdx >= 0) {
      updated = entries.map(e => (e.id === entry.id ? entry : e));
    } else {
      updated = [...entries, entry];
    }
    setEntries(updated);
    setShowSlotEditor(false);
    await persistEntries(updated);
  };

  const handleRemoveEntry = async (entryId) => {
    const updated = entries.filter(e => e.id !== entryId);
    setEntries(updated);
    setShowSlotEditor(false);
    await persistEntries(updated);
  };

  const handleAddExtraSlot = (newSlot) => {
    setExtraSlots(prev => [...prev, newSlot]);
  };

  // Advance to the next empty slot after the given index
  const advanceToNextEmptySlot = (afterIndex) => {
    const filledSlotIndices = new Set(dayEntries.map(e => e.slotIndex));
    for (const item of allSlots) {
      if (item.index > afterIndex && !filledSlotIndices.has(item.index) && item.index !== LUNCH_SLOT_INDEX) {
        setTimeout(() => {
          setActiveSlot(item.slot);
          setActiveSlotIndex(item.index);
          setShowSlotEditor(true);
        }, 300);
        return;
      }
    }
  };

  const statusClass = timetableStatus.toLowerCase();
  const statusIcon = timetableStatus === 'Published' ? '✓' : timetableStatus === 'Archived' ? '📦' : '📝';
  const statusLabel = timetableStatus === 'Published' ? 'Published' : timetableStatus === 'Archived' ? 'Archived' : 'Draft';

  const currentClass = classes.find(c => c.id === selectedClassId);

  // === Full-screen period detail page ===
  if (selectedEntry) {
    const { entry, slot, index } = selectedEntry;
    const info = entryTypeInfo(entry.entryType || 'subject');
    console.log('🔥 AdminTimetable: Showing detail for entry', entry.id, entry);
    return (
      <div className="admin-timetable detail-page">
        <button className="detail-back-button" onClick={() => setSelectedEntry(null)}>
          ◄ Back to Timetable
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{info.icon} {entry.subjectName || 'Period'}</h2>
              <p className="detail-subtitle">
                {entry.dayOfWeek || selectedDay} • Period {(index ?? entry.slotIndex ?? 0) + 1}
              </p>
            </div>
            <span className="detail-status-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
              {info.label}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Class</span>
              <span className="detail-value">{currentClass ? `${currentClass.name} (Grade ${currentClass.grade})` : 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Day</span>
              <span className="detail-value">{entry.dayOfWeek || selectedDay}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Period</span>
              <span className="detail-value">{(index ?? entry.slotIndex ?? 0) + 1}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Time</span>
              <span className="detail-value">{entry.startTime || slot?.start} – {entry.endTime || slot?.end}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Subject</span>
              <span className="detail-value">{entry.subjectName || 'N/A'}</span>
            </div>
            {(entry.teacherId || detailTeacherName) && (
              <div className="detail-field">
                <span className="detail-label">Teacher</span>
                <span className="detail-value">{detailTeacherName || entry.teacherId}</span>
              </div>
            )}
            {entry.room && (
              <div className="detail-field">
                <span className="detail-label">Room</span>
                <span className="detail-value">{entry.room}</span>
              </div>
            )}
            <div className="detail-field">
              <span className="detail-label">Timetable Status</span>
              <span className="detail-value">{statusLabel}</span>
            </div>
          </div>

          {!isReadOnly && index !== LUNCH_SLOT_INDEX && (
            <div className="detail-actions">
              <button
                className="detail-btn-primary"
                onClick={() => {
                  setSelectedEntry(null);
                  openSlotEditor({ start: entry.startTime || slot.start, end: entry.endTime || slot.end }, index ?? entry.slotIndex);
                }}
              >
                ✏️ Edit This Period
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-timetable">
      <div className="timetable-header">
        <h2>Timetable</h2>
        <p className="timetable-subtitle">Tap a slot to add or change a subject</p>
      </div>

      {classes.length === 0 ? (
        <div className="timetable-empty">
          <span className="empty-icon">📅</span>
          <p>No classes found.</p>
        </div>
      ) : (
        <>
          {/* Class picker (segmented, horizontally scrollable) */}
          <div className="class-picker">
            <label className="picker-label">Class</label>
            <div className="chip-row">
              {classes.map(cls => (
                <button
                  key={cls.id}
                  className={`chip ${selectedClassId === cls.id ? 'active' : ''}`}
                  onClick={() => setSelectedClassId(cls.id)}
                >
                  {cls.name} (Grade {cls.grade})
                </button>
              ))}
            </div>
          </div>

          {selectedClassId && (
            <div className={`status-banner ${statusClass}`}>
              <span className="status-icon">{statusIcon}</span>
              <span className="status-text">{statusLabel}</span>
              {publishedAt && (
                <span className="status-meta">
                  Published: {new Date(publishedAt?.seconds ? publishedAt.seconds * 1000 : publishedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Day picker (segmented, horizontally scrollable) */}
          <div className="day-picker">
            <div className="chip-row">
              {WEEKDAYS.map((day, i) => {
                const today = new Date().getDay();
                const isToday = today === i + 1; // 1=Mon, ..., 5=Fri
                return (
                <button
                  key={day}
                  className={`chip day-chip ${selectedDay === day ? 'active' : ''}`}
                  onClick={() => setSelectedDay(day)}
                >
                  {day.slice(0, 3)}
                  {isToday && <span className="today-dot" style={{ display: 'block', width: 5, height: 5, borderRadius: '50%', background: selectedDay === day ? '#fff' : '#22c55e', margin: '2px auto 0' }} />}
                </button>
                );
              })}
            </div>
          </div>

          <div className="timetable-day-content">
            <div className="day-header">
              <h3>{selectedDay} Timetable</h3>
              {isReadOnly && <span className="readonly-badge">Read Only</span>}
            </div>

            {loading ? (
              <div className="timetable-loading">
                <div className="spinner"></div>
                <p>Loading timetable...</p>
              </div>
            ) : (
              <div className="slots-list">
                {allSlots.map(({ slot, index }) => {
                  const entry = entriesBySlotIndex[index];
                  const isLunchSlot = index === LUNCH_SLOT_INDEX;
                  const lunchEntry = isLunchSlot ? (entry || {
                    id: `lunch-${selectedDay}-${index}`,
                    dayOfWeek: selectedDay,
                    subjectName: 'Lunch',
                    startTime: slot.start,
                    endTime: slot.end,
                    slotIndex: index,
                    entryType: 'lunch'
                  }) : null;
                  return (
                    <SlotRow
                      key={`${index}-${slot.start}`}
                      slot={slot}
                      entry={isLunchSlot ? lunchEntry : entry}
                      isLunchSlot={isLunchSlot}
                      isReadOnly={isReadOnly}
                      onTap={() => {
                        if (entry) {
                          setSelectedEntry({ entry, slot, index });
                        } else {
                          openSlotEditor(slot, index);
                        }
                      }}
                    />
                  );
                })}
                {extraEntries.map(entry => (
                  <SlotRow
                    key={entry.id}
                    slot={{ start: entry.startTime, end: entry.endTime }}
                    entry={entry}
                    isReadOnly={isReadOnly}
                    onTap={() => setSelectedEntry({ entry, slot: { start: entry.startTime, end: entry.endTime }, index: entry.slotIndex })}
                  />
                ))}
                {!isReadOnly && (
                  <button className="add-extra-slot-btn" onClick={() => {
                    const lastEnd = extraSlots.length > 0 ? extraSlots[extraSlots.length - 1].end : (TIME_SLOTS[TIME_SLOTS.length - 1].end);
                    const start = time12hToDate(lastEnd);
                    const end = new Date(start.getTime() + 60 * 60000);
                    handleAddExtraSlot({ start: dateToTime12h(start), end: dateToTime12h(end) });
                  }}>
                    <span className="add-icon">＋</span>
                    Add Extra Slot
                  </button>
                )}
              </div>
            )}
            {error && <p className="timetable-error">{error}</p>}
            {success && <p className="timetable-success">{success}</p>}
          </div>

          {selectedClassId && (
            <div className="timetable-actions">
              {actionLoading && <div className="spinner"></div>}
              {isReadOnly ? (
                timetableStatus === 'Published' && (
                  <>
                    <button className="action-button revert" onClick={() => setShowRevertConfirm(true)} disabled={actionLoading}>
                      ↺ Revert to Draft
                    </button>
                    <button className="action-button archive" onClick={() => setShowArchiveConfirm(true)} disabled={actionLoading}>
                      📦 Archive Timetable
                    </button>
                  </>
                )
              ) : (
                <button className="action-button publish" onClick={() => setShowPublishConfirm(true)} disabled={actionLoading || entries.length === 0}>
                  ✓ Publish Timetable
                </button>
              )}
            </div>
          )}
        </>
      )}

      {showSlotEditor && activeSlot && (
        <SlotEditorModal
          day={selectedDay}
          slot={activeSlot}
          slotIndex={activeSlotIndex}
          classId={selectedClassId}
          schoolId={schoolId}
          currentClass={currentClass}
          existingEntry={entriesBySlotIndex[activeSlotIndex]}
          onClose={() => setShowSlotEditor(false)}
          onSave={handleSaveEntry}
          onRemove={handleRemoveEntry}
          onAdvanceToNextSlot={() => advanceToNextEmptySlot(activeSlotIndex)}
        />
      )}

      {showPublishConfirm && (
        <div className="confirm-overlay" onClick={() => setShowPublishConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Publish Timetable?</h3>
            <p>Once published, teachers will be able to view this timetable but not edit it. You can revert to draft later if needed.</p>
            <div className="confirm-actions">
              <button className="cancel-btn" onClick={() => setShowPublishConfirm(false)}>Cancel</button>
              <button className="confirm-btn publish" onClick={handlePublish}>Publish</button>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirm && (
        <div className="confirm-overlay" onClick={() => setShowArchiveConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Archive Timetable?</h3>
            <p>Archived timetables are hidden from teachers and parents. This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="cancel-btn" onClick={() => setShowArchiveConfirm(false)}>Cancel</button>
              <button className="confirm-btn archive" onClick={handleArchive}>Archive</button>
            </div>
          </div>
        </div>
      )}

      {showRevertConfirm && (
        <div className="confirm-overlay" onClick={() => setShowRevertConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Revert to Draft?</h3>
            <p>Reverting to draft will allow editing again. Teachers will lose read access until republished.</p>
            <div className="confirm-actions">
              <button className="cancel-btn" onClick={() => setShowRevertConfirm(false)}>Cancel</button>
              <button className="confirm-btn revert" onClick={handleRevert}>Revert</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Slot row — matches iOS TimeSlotRow
function SlotRow({ slot, entry, isLunchSlot, isReadOnly, onTap }) {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = timeToMinutes(entry?.startTime || slot.start);
  const endMinutes = timeToMinutes(entry?.endTime || slot.end);
  const isCurrent = entry && startMinutes != null && endMinutes != null && nowMinutes >= startMinutes && nowMinutes <= endMinutes;

  const entryType = entry?.entryType || 'subject';
  const info = entryTypeInfo(entryType);

  const entryColorClass = entry ? `entry-${info.color}` : 'entry-empty';

  return (
    <div
      className={`slot-row ${entryColorClass} ${isCurrent ? 'is-current' : ''} ${isLunchSlot ? 'is-lunch' : ''}`}
      onClick={() => { if (!isLunchSlot && (entry || !isReadOnly)) onTap(); }}
    >
      <div className="slot-main">
        <span className={`slot-time ${isCurrent ? 'current-time' : ''}`}>
          {entry?.startTime || slot.start} - {entry?.endTime || slot.end}
        </span>
        {entry ? (
          <div className="slot-entry">
            {!entry.entryType || entry.entryType === 'subject' ? null : (
              <span className="entry-type-icon">{info.icon}</span>
            )}
            <span className="entry-name">{entry.subjectName}</span>
            {entry.room && entry.entryType === 'subject' && (
              <span className="entry-room">Room: {entry.room}</span>
            )}
          </div>
        ) : (
          <span className="entry-free">Free</span>
        )}
      </div>
      <div className="slot-trailing">
        {isCurrent && <span className="now-badge">NOW</span>}
        {!isLunchSlot && !isReadOnly && (
          <span className="slot-chevron">{entry ? '›' : '＋'}</span>
        )}
      </div>
    </div>
  );
}

// Slot editor modal — matches iOS AdminSubjectPickerSheet
function SlotEditorModal({ day, slot, slotIndex, classId, schoolId, currentClass, existingEntry, onClose, onSave, onRemove, onAdvanceToNextSlot }) {
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedEntryType, setSelectedEntryType] = useState(existingEntry?.entryType || 'subject');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [startTime, setStartTime] = useState(existingEntry?.startTime || slot.start);
  const [endTime, setEndTime] = useState(existingEntry?.endTime || slot.end);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomBuilding, setNewRoomBuilding] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoadingData(true);
      try {
        const ck = ckFor(schoolId);
        // Load subjects from all 3 locations (global + class subcollection + grade subcollection)
        const allSubjects = [];
        const seenSubjectIds = new Set();

        // 1. Global subjects (cached; client-side scope is case-insensitive)
        const globalDocs = scopeBySchool(
          await dataCacheService.getDocs(ck('subjects'), collection(db, 'subjects')),
          schoolId
        );
        globalDocs.forEach(data => {
          if (!seenSubjectIds.has(data.id)) { seenSubjectIds.add(data.id); allSubjects.push(data); }
        });

        // 2. Class-specific subjects (classes/{classId}/subjects)
        if (classId) {
          const classSubDocs = await dataCacheService.getDocs(ck(`classes/${classId}/subjects`), collection(db, 'classes', classId, 'subjects'));
          classSubDocs.forEach(data => {
            if (!seenSubjectIds.has(data.id)) { seenSubjectIds.add(data.id); allSubjects.push(data); }
          });
        }

        // 3. Grade-level subjects (gradeLevels/{gradeId}/subjects) (parallel, cached)
        const gradeDocs = scopeBySchool(
          await dataCacheService.getDocs(ck('gradeLevels'), collection(db, 'gradeLevels')),
          schoolId
        );
        const gradeSubDocs = await Promise.all(gradeDocs.map(g =>
          dataCacheService.getDocs(ck(`gradeLevels/${g.id}/subjects`), collection(db, 'gradeLevels', g.id, 'subjects'))
        ));
        gradeSubDocs.flat().forEach(data => {
          if (!seenSubjectIds.has(data.id)) { seenSubjectIds.add(data.id); allSubjects.push(data); }
        });

        if (cancelled) return;
        // Filter to active subjects for this class (or all if no classId match)
        const classSubjects = allSubjects
          .filter(s => s.isActive !== false && (!classId || s.classId === classId || !s.classId))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSubjects(classSubjects);

        // Load teachers (users with role=Teacher)
        const usersData = scopeBySchool(
          await dataCacheService.getDocs(ck('users'), collection(db, 'users')),
          schoolId
        );
        const teachersData = usersData.filter(u => u.role === 'Teacher');
        if (cancelled) return;
        setTeachers(teachersData);

        // Load rooms
        const roomsData = scopeBySchool(
          await dataCacheService.getDocs(ck('rooms'), collection(db, 'rooms')),
          schoolId
        )
          .filter(r => r.isActive !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (cancelled) return;
        setRooms(roomsData);

        // Initialize selection from existing entry
        setSelectedEntryType(existingEntry?.entryType || 'subject');
        const matchedSubject = classSubjects.find(s =>
          (existingEntry?.subjectId && s.id === existingEntry.subjectId) ||
          (!existingEntry?.subjectId && s.name === existingEntry?.subjectName)
        ) || null;
        setSelectedSubject(matchedSubject);
        setSelectedRoomId(existingEntry?.roomId || (existingEntry?.room ? roomsData.find(r => r.name === existingEntry.room)?.id : null) || null);
        setSelectedTeacherId(existingEntry?.teacherId || null);
        setStartTime(existingEntry?.startTime || slot.start);
        setEndTime(existingEntry?.endTime || slot.end);
      } catch (err) {
        console.error('SlotEditorModal: error loading data', err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, [classId, schoolId, slot.start, slot.end, existingEntry]);

  const availableSubjects = subjects;

  const handleSelectSubject = (subject) => {
    setSelectedSubject(subject);
    // Auto-assign teacher from class subjectTeacherAssignments or homeroom teacher
    if (!existingEntry) {
      const assigned = currentClass?.subjectTeacherAssignments?.[subject.name];
      const homeroom = currentClass?.teacherId;
      setSelectedTeacherId(assigned || homeroom || null);
    }
  };

  const handleSaveRoom = async () => {
    const name = newRoomName.trim();
    if (!name) return;
    const roomId = crypto.randomUUID();
    const roomData = {
      id: roomId,
      name,
      building: newRoomBuilding.trim() || null,
      schoolId: schoolId || null,
      isActive: true,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(db, 'rooms', roomId), roomData);
      dataCacheService.invalidatePath('rooms');
      setRooms(prev => [...prev, roomData].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setSelectedRoomId(roomId);
      setNewRoomName('');
      setNewRoomBuilding('');
      setShowAddRoom(false);
    } catch (err) {
      console.error('Error adding room', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedEntryType === 'subject') {
        if (!selectedSubject) { setSaving(false); return; }
        const selectedRoom = rooms.find(r => r.id === selectedRoomId);
        const entry = {
          id: existingEntry?.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          dayOfWeek: day,
          subjectName: selectedSubject.name,
          subjectId: selectedSubject.id,
          teacherId: selectedTeacherId || null,
          startTime: startTime.trim(),
          endTime: endTime.trim(),
          room: selectedRoom?.name || '',
          roomId: selectedRoom?.id || null,
          slotIndex,
          entryType: 'subject'
        };
        await onSave(entry);
        if (!existingEntry) onAdvanceToNextSlot();
      } else {
        // Lunch or Break
        const entryName = selectedEntryType === 'lunch' ? 'Lunch' : 'Break';
        const entry = {
          id: existingEntry?.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          dayOfWeek: day,
          subjectName: entryName,
          subjectId: null,
          teacherId: null,
          startTime: startTime.trim(),
          endTime: endTime.trim(),
          room: '',
          roomId: null,
          slotIndex,
          entryType: selectedEntryType
        };
        await onSave(entry);
        if (!existingEntry) onAdvanceToNextSlot();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (existingEntry) {
      await onRemove(existingEntry.id);
    }
  };

  const getTeacherName = (id) => {
    if (!id) return null;
    const t = teachers.find(t => t.id === id);
    return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.username : null;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="slot-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{day.slice(0, 3)} • Period {slotIndex + 1}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Entry type segmented picker */}
        <div className="entry-type-picker">
          {ENTRY_TYPES.map(t => (
            <button
              key={t.value}
              className={`entry-type-chip ${selectedEntryType === t.value ? `active ${t.color}` : ''}`}
              onClick={() => {
                setSelectedEntryType(t.value);
                if (t.value !== 'subject') {
                  setStartTime(slot.start);
                  setEndTime(slot.end);
                }
              }}
            >
              <span className="entry-type-chip-icon">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {loadingData ? (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : selectedEntryType === 'subject' ? (
          <>
            {/* Time inputs */}
            <div className="time-row">
              <div className="time-field">
                <label>Start Time</label>
                <input type="time" value={toInputTime(startTime)} onChange={(e) => setStartTime(fromInputTime(e.target.value))} />
              </div>
              <div className="time-field">
                <label>End Time</label>
                <input type="time" value={toInputTime(endTime)} onChange={(e) => setEndTime(fromInputTime(e.target.value))} />
              </div>
            </div>

            {selectedSubject ? (
              <div className="selected-subject-bar">
                <div>
                  <span className="selected-label">Selected Subject</span>
                  <span className="selected-name">{selectedSubject.name}</span>
                </div>
                <button className="change-btn" onClick={() => setSelectedSubject(null)}>Change</button>
              </div>
            ) : (
              <div className="subject-list-section">
                <h4>Select Subject</h4>
                {availableSubjects.length === 0 ? (
                  <div className="empty-subjects">
                    <span className="empty-icon">📚</span>
                    <p>No subjects found for this class</p>
                    <p className="empty-hint">Add subjects via Subject Management first</p>
                  </div>
                ) : (
                  <div className="subject-list">
                    {availableSubjects.map(subject => {
                      const assignedTeacherId = currentClass?.subjectTeacherAssignments?.[subject.name] || currentClass?.teacherId;
                      const teacherName = getTeacherName(assignedTeacherId);
                      return (
                        <button
                          key={subject.id}
                          className={`subject-item ${selectedSubject?.id === subject.id ? 'selected' : ''}`}
                          onClick={() => handleSelectSubject(subject)}
                        >
                          <div className="subject-item-main">
                            <span className="subject-item-name">{subject.name}</span>
                            <span className="subject-item-cat">{subject.category || ''}</span>
                          </div>
                          {teacherName && (
                            <span className="subject-item-teacher">{teacherName}</span>
                          )}
                          {selectedSubject?.id === subject.id && <span className="check-icon">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {selectedSubject && (
              <div className="room-section">
                <div className="room-header">
                  <h4>Room (optional)</h4>
                  <button className="add-room-btn" onClick={() => setShowAddRoom(true)}>+ Add Room</button>
                </div>
                {showAddRoom && (
                  <div className="add-room-form">
                    <input type="text" placeholder="Room name" value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)} />
                    <input type="text" placeholder="Building (optional)" value={newRoomBuilding} onChange={(e) => setNewRoomBuilding(e.target.value)} />
                    <button className="save-room-btn" onClick={handleSaveRoom} disabled={!newRoomName.trim()}>Add</button>
                  </div>
                )}
                <div className="room-list">
                  <button className={`room-item ${!selectedRoomId ? 'selected' : ''}`} onClick={() => setSelectedRoomId(null)}>
                    <span>No room assigned</span>
                    {!selectedRoomId && <span className="check-icon">✓</span>}
                  </button>
                  {rooms.map(room => (
                    <button key={room.id} className={`room-item ${selectedRoomId === room.id ? 'selected' : ''}`} onClick={() => setSelectedRoomId(room.id)}>
                      <div>
                        <span>{room.name}</span>
                        {room.building && <span className="room-building">{room.building}</span>}
                      </div>
                      {selectedRoomId === room.id && <span className="check-icon">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          // Lunch / Break view
          <div className="break-lunch-view">
            <span className="break-lunch-icon" style={{ color: entryTypeInfo(selectedEntryType).color === 'green' ? '#2e7d32' : '#7b1fa2' }}>
              {entryTypeInfo(selectedEntryType).icon}
            </span>
            <h4>{entryTypeInfo(selectedEntryType).label}</h4>
            <p className="break-time-preview">{startTime} – {endTime}</p>
            <p className="break-slot-hint">This will fill the {slot.start} – {slot.end} slot</p>
            <div className="time-row">
              <div className="time-field">
                <label>Start Time</label>
                <input type="time" value={toInputTime(startTime)} onChange={(e) => setStartTime(fromInputTime(e.target.value))} />
              </div>
              <div className="time-field">
                <label>End Time</label>
                <input type="time" value={toInputTime(endTime)} onChange={(e) => setEndTime(fromInputTime(e.target.value))} />
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          {existingEntry && (
            <button className="remove-entry-btn" onClick={handleRemove} disabled={saving}>
              🗑️ Remove from Timetable
            </button>
          )}
          <button className="cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          {selectedEntryType === 'subject' && (
            <button className="assign-btn" onClick={handleSave} disabled={saving || !selectedSubject}>
              {saving ? 'Saving...' : (existingEntry ? 'Update' : 'Assign Subject')}
            </button>
          )}
          {selectedEntryType !== 'subject' && (
            <button className="assign-btn" onClick={handleSave} disabled={saving}
              style={{ background: entryTypeInfo(selectedEntryType).color === 'green' ? '#2e7d32' : '#7b1fa2' }}>
              {saving ? 'Saving...' : `Add ${entryTypeInfo(selectedEntryType).label}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Convert "h:mm AM/PM" → "HH:mm" for <input type="time">
function toInputTime(time12h) {
  const mins = timeToMinutes(time12h);
  if (mins == null) return '08:30';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

// Convert "HH:mm" → "h:mm AM/PM"
function fromInputTime(inputTime) {
  if (!inputTime) return '';
  const [hStr, mStr] = inputTime.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h < 12 ? 'AM' : 'PM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${period}`;
}

export default AdminTimetableView;
